// CRM Type Definitions for DriveSync CRM
// These types mirror the Supabase schema for the CRM tables

// Ontario HST rate — change here if province changes
export const HST_RATE = 0.13;
export const HST_DIVISOR = 113; // For extracting HST from tax-inclusive amounts (amount * 13 / 113)

export type DealStage = 'prospect' | 'negotiation' | 'financed' | 'delivered' | 'funded' | 'lost';
export type LeadScore = 'hot' | 'warm' | 'cold';
export type ActivityType = 'note' | 'call' | 'email' | 'sms' | 'meeting' | 'test_drive' | 'follow_up' | 'status_change' | 'system';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type LeadSource = 'website' | 'google_ads' | 'facebook' | 'instagram' | 'walk_in' | 'referral' | 'autotrader' | 'kijiji' | 'phone' | 'other';

export interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  date_of_birth: string | null;
  credit_rating: string | null;
  lead_score: LeadScore;
  lead_source: LeadSource;
  assigned_to: string | null;
  tags: string[];
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_url: string | null;
  landing_page: string | null;
  last_contacted_at: string | null;
  next_follow_up_at: string | null;
  lifetime_value: number;
  sms_opted_in: boolean;
  email_opted_in: boolean;
  preferred_contact: string | null;
  google_review_sent_at: string | null;
  google_review_reminded_at: string | null;
  birthday_greeted_at: string | null;
  referral_asked_at: string | null;
  referred_by: string | null;
  created_at: string;
  updated_at: string;
  driver_license: string | null;
  driver_license_expiry: string | null;
}

export interface CustomerInterestedVehicle {
  id: string;
  customer_id: string;
  vehicle_id: string;
  created_at: string;
}

export interface CustomerInsert {
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  date_of_birth?: string | null;
  credit_rating?: string | null;
  lead_score?: LeadScore;
  lead_source?: LeadSource;
  assigned_to?: string | null;
  tags?: string[];
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  referrer_url?: string | null;
  landing_page?: string | null;
  last_contacted_at?: string | null;
  next_follow_up_at?: string | null;
  driver_license?: string | null;
  driver_license_expiry?: string | null;
}

export interface Message {
  id: string;
  customer_id: string;
  direction: 'inbound' | 'outbound';
  channel: 'sms' | 'email' | 'whatsapp';
  body: string;
  from_number: string | null;
  to_number: string | null;
  twilio_sid: string | null;
  status: string;
  sent_by: string | null;
  read_at: string | null;
  created_at: string;
}

export interface MessageTemplate {
  id: string;
  name: string;
  category: string;
  channel: string;
  body: string;
  is_active: boolean;
  created_at: string;
}

export interface Deal {
  id: string;
  deal_number: string;
  customer_id: string;
  vehicle_id: string | null;
  stage: DealStage;
  title: string;
  sale_price: number | null;
  cost_price: number | null;
  front_gross: number | null;
  trade_in_value: number | null;
  down_payment: number | null;
  lender: string | null;
  interest_rate: number | null;
  finance_term: number | null;
  monthly_payment: number | null;
  fi_notes: string | null;
  fi_total: number;
  notes: string | null;
  lost_reason: string | null;
  assigned_to: string | null;
  priority: TaskPriority;
  expected_close_date: string | null;
  closed_at: string | null;
  delivery_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface DealInsert {
  customer_id: string;
  vehicle_id?: string | null;
  stage?: DealStage;
  title: string;
  sale_price?: number | null;
  cost_price?: number | null;
  front_gross?: number | null;
  trade_in_value?: number | null;
  down_payment?: number | null;
  lender?: string | null;
  interest_rate?: number | null;
  finance_term?: number | null;
  monthly_payment?: number | null;
  fi_notes?: string | null;
  fi_total?: number;
  notes?: string | null;
  assigned_to?: string | null;
  priority?: TaskPriority;
  expected_close_date?: string | null;
  delivery_date?: string | null;
}

export interface Activity {
  id: string;
  customer_id: string;
  deal_id: string | null;
  vehicle_id: string | null;
  type: ActivityType;
  subject: string;
  description: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
}

export interface ActivityInsert {
  customer_id: string;
  deal_id?: string | null;
  vehicle_id?: string | null;
  type: ActivityType;
  subject: string;
  description?: string | null;
  metadata?: Record<string, unknown>;
  created_by?: string | null;
}

export interface Task {
  id: string;
  customer_id: string | null;
  deal_id: string | null;
  title: string;
  description: string | null;
  due_date: string;
  completed_at: string | null;
  priority: TaskPriority;
  assigned_to: string | null;
  is_automated: boolean;
  created_at: string;
}

export interface TaskInsert {
  customer_id?: string | null;
  deal_id?: string | null;
  title: string;
  description?: string | null;
  due_date: string;
  priority?: TaskPriority;
  assigned_to?: string | null;
  is_automated?: boolean;
}

// Pipeline stages config
export const DEAL_STAGES: { key: DealStage; label: string; color: string }[] = [
  { key: 'prospect', label: 'Prospect', color: '#64748b' },
  { key: 'negotiation', label: 'Negotiation', color: '#f59e0b' },
  { key: 'financed', label: 'Financed', color: '#3b82f6' },
  { key: 'delivered', label: 'Delivered', color: '#8b5cf6' },
  { key: 'funded', label: 'Funded', color: '#10b981' },
  { key: 'lost', label: 'Lost', color: '#ef4444' },
];

export const LEAD_SCORES: { key: LeadScore; label: string; color: string; bg: string; border: string }[] = [
  { key: 'hot', label: 'Hot', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  { key: 'warm', label: 'Warm', color: '#f59e0b', bg: '#fef3c7', border: '#fde68a' },
  { key: 'cold', label: 'Cold', color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' },
];

export const LEAD_SOURCES: { key: LeadSource; label: string }[] = [
  { key: 'website', label: 'Website' },
  { key: 'google_ads', label: 'Google Ads' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'walk_in', label: 'Walk-in' },
  { key: 'referral', label: 'Referral' },
  { key: 'autotrader', label: 'AutoTrader' },
  { key: 'kijiji', label: 'Kijiji' },
  { key: 'phone', label: 'Phone' },
  { key: 'other', label: 'Other' },
];

export const TASK_PRIORITIES: { key: TaskPriority; label: string; color: string }[] = [
  { key: 'low', label: 'Low', color: '#64748b' },
  { key: 'medium', label: 'Medium', color: '#3b82f6' },
  { key: 'high', label: 'High', color: '#f59e0b' },
  { key: 'urgent', label: 'Urgent', color: '#ef4444' },
];

// Helpers
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function getLeadScoreConfig(score: LeadScore) {
  return LEAD_SCORES.find(s => s.key === score) || LEAD_SCORES[2];
}

export function getDealStageConfig(stage: DealStage) {
  return DEAL_STAGES.find(s => s.key === stage) || DEAL_STAGES[0];
}

export function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '-';
  return '$' + amount.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
